import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'astroturf/react';

import { Card, Header, ViewAll } from './common';
import AuthorPopover from './AuthorPopover';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import {
  ImpressionEntity,
  getImpressionId,
} from 'js/utils/impressions/watchImpressions';
import { User, Work } from './hooks/types';

export interface Props {
  // Whether to use the alternate clean look (part of swp_clean_rr ab test)
  altLook?: boolean;
  // If given, headerText should be localized
  headerText?: string;
  // How many works to show before showing a View More button
  limit?: number;
  onDownload: (work: Work) => void;
  // `onFollow` and `onMessage` are used for the author popover
  onFollow: (author: User) => void;
  onMessage: (author: User) => void;
  onReport: (work: Work, sourceWorkId?: number | string) => void;
  onSave: (work: Work) => void;
  // If this is not specified, clicking View All
  // simply shows all works
  onViewAll?: () => void;
  works: Work[];
  // The id of the source work the 'works' are related to
  // Should exist if these are rendered, unless on a non-swp somehow
  sourceWorkId?: number | string;
  impressionSource: string;
}

type AuthorProps = {
  altLook?: boolean;
  authors: User[];
  onFollow: Props['onFollow'];
  onMessage: Props['onMessage'];
};

type DropdownProps = {
  bookmarked: boolean;
  onSave: Props['onSave'];
  onReport: Props['onReport'];
};

const RIGHT_ARROW = '\u25b8';

const ButtonGroup = styled.div`
  display: flex;
`;

const Container = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DownArrow = styled('i').attrs({
  className: 'fa fa-arrow-down',
})`
  margin-right: 4px;
`;

const DownloadLink = styled.a`
  color: #777;
  font-size: 12px;
  margin-right: 16px;
  cursor: pointer;
  position: relative;

  &:hover {
    color: #0954d3;
    text-decoration: none;
  }
`;

const DropdownContainer = styled.div`
  background: white;
  box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.15);
  position: absolute;
  right: -28px;
  top: 20px;
  z-index: 2;
`;

const DropdownOption = styled.div`
  color: #777;
  cursor: pointer;
  padding: 12px 16px;
  text-align: left;

  &:hover {
    background: #ddd;
  }
`;

const Ellipsis = styled('i').attrs({
  className: 'fa fa-ellipsis-h',
})`
  margin-right: 4px;
`;

const Info = styled.div<{ altLook?: boolean }>`
  color: #333;
  font-size: 13px;
  margin: 0 4px 0 0;

  &:not(:last-of-type)::after {
    content: '\\2022';
    margin-left: 4px;
  }

  &.altLook {
    color: #888;
    font-size: 12px;
    margin-right: 6px;
  }

  &:not(:last-of-type).altLook::after {
    color: #ccc;
    font-size: 10px;
    margin-left: 6px;
  }
`;

const InfoGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const MoreButton = styled.button`
  background: transparent;
  border: 0;
  color: #777;
  font-size: 12px;
  position: relative;
`;

const Name = styled.a<{ altLook?: boolean; nohover?: boolean }>`
  color: #4b4b4b;
  position: relative;
  text-decoration: none;

  &.nohover:hover {
    text-decoration: none;
  }

  &:hover {
    color: #4b4b4b;
    text-decoration: underline;
  }

  &.altLook {
    color: #888;
  }
`;

// aspect ratio of width:height to match
// typical page dimensions
// non-conforming thumbnail images will be
// automatically stretched to fit
const Thumbnail = styled.img`
  border: 1px solid #eee;
  height: 88px;
  width: 69px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  margin: 0 0 4px 0;

  a {
    color: #4b4b4b;
    position: relative;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const WorkDiv = styled.div<{ altLook?: boolean }>`
  align-items: center;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  padding: 12px;
  transition: 160ms background-color;
  position: relative;

  &.altLook {
    border-bottom: 1px solid transparent;
    position: relative;
  }

  // With altLook, there's a border bottom that doesn't go all the way to the left edge.
  // We can't use a left-margin because we want the hover effect to take
  // up the entire space, so we have to use an after element for the bottom border.
  &.altLook::after {
    background: #ddd;
    bottom: 0;
    content: '';
    left: 12px;
    height: 1px;
    position: absolute;
    width: calc(100% - 12px);
  }

  &.altLook:last-of-type {
    border-bottom: 0;
    &::after {
      background: none;
    }
  }

  &:hover {
    background-color: #f8f8f8;
  }
`;

const WorkDivBackgroundLink = styled.a`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
`;

const Dropdown = ({ bookmarked, onSave, onReport }: DropdownProps) => (
  <DropdownContainer>
    {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
    <DropdownOption onClick={onSave}>
      {bookmarked ? 'Bookmarked' : 'Bookmark'}
    </DropdownOption>
    {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
    <DropdownOption onClick={onReport}>Report</DropdownOption>
  </DropdownContainer>
);

const Authors = ({ altLook, authors, onFollow, onMessage }: AuthorProps) => {
  // If null, no popover should show
  // Otherwise, this is the index of the author to show the popover for
  const [popoverIndex, setPopoverIndex] = useState<number | null>(null);

  if (authors.length === 0) return null;

  const allButLast = authors.slice(0, authors.length - 1);

  if (authors.length === 1) {
    const author = authors[0];
    return (
      <Name
        altLook={altLook}
        href={author.url}
        nohover={!author.url}
        onMouseEnter={() => setPopoverIndex(0)}
        onMouseLeave={() => setPopoverIndex(null)}
      >
        {popoverIndex === 0 && author.url && (
          <AuthorPopover
            {...author}
            onFollow={() => onFollow(author)}
            onMessage={() => onMessage(author)}
          />
        )}
        {author.displayName}
      </Name>
    );
  }

  if (authors.length === 2) {
    const a0 = authors[0];
    const a1 = authors[1];

    return (
      <React.Fragment>
        <Name
          altLook={altLook}
          href={a0.url}
          nohover={!a0.url}
          onMouseEnter={() => setPopoverIndex(0)}
          onMouseLeave={() => setPopoverIndex(null)}
        >
          {popoverIndex === 0 && a0.url && (
            <AuthorPopover
              {...a0}
              onFollow={() => onFollow(a0)}
              onMessage={() => onMessage(a0)}
            />
          )}
          {a0.displayName}
        </Name>{' '}
        and{' '}
        <Name
          altLook={altLook}
          href={a1.url}
          nohover={!a1.url}
          onMouseEnter={() => setPopoverIndex(1)}
          onMouseLeave={() => setPopoverIndex(null)}
        >
          {popoverIndex === 1 && a1.url && (
            <AuthorPopover
              {...a1}
              onFollow={() => onFollow(a1)}
              onMessage={() => onMessage(a1)}
            />
          )}
          {a1.displayName}
        </Name>
      </React.Fragment>
    );
  }

  const last = authors[authors.length - 1];
  const lastIndex = authors.length - 1;
  return (
    <React.Fragment>
      {allButLast.map((author, i) => (
        <React.Fragment key={i}>
          <Name
            altLook={altLook}
            href={author.url}
            nohover={!author.url}
            onMouseEnter={() => setPopoverIndex(i)}
            onMouseLeave={() => setPopoverIndex(null)}
          >
            {popoverIndex === i && author.url && (
              <AuthorPopover
                {...author}
                onFollow={() => onFollow(author)}
                onMessage={() => onMessage(author)}
              />
            )}
            {author.displayName}
          </Name>
          {', '}
        </React.Fragment>
      ))}
      {'and '}
      <Name
        altLook={altLook}
        href={last.url}
        onMouseEnter={() => setPopoverIndex(lastIndex)}
        onMouseLeave={() => setPopoverIndex(null)}
      >
        {popoverIndex === lastIndex && (
          <AuthorPopover {...last} onFollow={() => {}} onMessage={() => {}} />
        )}
        {last.displayName}
      </Name>
    </React.Fragment>
  );
};

const RelatedWorksCard: React.FC<Props> = ({
  altLook,
  headerText,
  limit: initialLimit = Infinity,
  onDownload,
  onFollow,
  onMessage,
  onReport,
  onSave,
  onViewAll,
  works,
  sourceWorkId,
  impressionSource,
}: Props) => {
  const [limit, setLimit] = useState(initialLimit);
  // null if none are open, the index of the dropdown to show
  // if a dropdown is open
  const [showDropdown, setShowDropdown] = useState<number | null>(null);
  const workContainer = useRef<HTMLDivElement[]>([]);
  useEffect(() => {
    const callback = () => {
      setShowDropdown(null);
    };

    workContainer.current.forEach((el) =>
      el.addEventListener('mouseleave', callback),
    );
    return () => {
      // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
      // eslint-disable-next-line react-hooks/exhaustive-deps
      workContainer.current.forEach((el) =>
        el.removeEventListener('mouseleave', callback),
      );
    };
  }, [showDropdown]);

  let translatedHeaderText = headerText;
  if (translatedHeaderText == null) translatedHeaderText = 'Related Papers';

  const save = (work: Work) => () => {
    // TODO (Zanchi): Make dropdown actualy hide on save/report
    setShowDropdown(null);
    onSave(work);
  };
  const report = (work: Work) => () => {
    setShowDropdown(null);
    onReport(work, sourceWorkId);
  };

  const limited = works.slice(0, limit);
  const showViewAll =
    limited.length !== works.length || typeof onViewAll === 'function';
  const viewAll = (
    <ViewAll
      altLook={altLook}
      onClick={() => {
        typeof onViewAll === 'function' ? onViewAll() : setLimit(Infinity);
      }}
    >
      View All {RIGHT_ARROW}
    </ViewAll>
  );

  const impressionIds = useMemo(
    () => works.map(() => getImpressionId()),
    [works],
  );

  return (
    <Card altLook={altLook}>
      <Header altLook={altLook}>
        {translatedHeaderText} {altLook && showViewAll && viewAll}
      </Header>
      {limited.map((work, i) => {
        const isOpenalexWork = work.type === 'OpenalexWork';
        const impressionId = impressionIds[i];
        const workUrl = isOpenalexWork
          ? work.internal_url
          : appendUrlParams(work.internal_url, {
              nav_from: impressionId,
              rw_pos: String(i),
            });
        const workTarget = isOpenalexWork ? '_blank' : undefined;
        return (
          <React.Fragment key={i}>
            <ImpressionWatcher
              source={impressionSource}
              entity_id={work.id}
              extra={{ position: i, source: sourceWorkId }}
              entity_type={
                isOpenalexWork
                  ? ImpressionEntity.OpenalexWork
                  : ImpressionEntity.Work
              }
              impressionId={impressionId}
            >
              <WorkDiv
                altLook={altLook}
                key={work.id}
                ref={(el: HTMLDivElement) => (workContainer.current[i] = el)}
              >
                <WorkDivBackgroundLink href={workUrl} target={workTarget} />
                <Container>
                  <div>
                    <Title>
                      <a href={workUrl} target={workTarget}>
                        {work.title}
                      </a>
                    </Title>
                    <InfoGroup>
                      <Info altLook={altLook}>
                        <Authors
                          altLook={altLook}
                          authors={work.authors}
                          onFollow={onFollow}
                          onMessage={onMessage}
                        />
                      </Info>
                    </InfoGroup>
                  </div>

                  <ButtonGroup>
                    {work.attachments?.length > 0 && (
                      <DownloadLink
                        onClick={() => {
                          onDownload(work);
                        }}
                      >
                        <DownArrow />
                        Download
                      </DownloadLink>
                    )}
                    {!isOpenalexWork && (
                      <MoreButton onClick={() => setShowDropdown(i)}>
                        <Ellipsis />
                        More Options
                        {showDropdown === i && (
                          <Dropdown
                            bookmarked={work.bookmarkId != null}
                            onSave={save(work)}
                            onReport={report(work)}
                          />
                        )}
                      </MoreButton>
                    )}
                  </ButtonGroup>
                </Container>
                <Thumbnail src={work.image} />
              </WorkDiv>
            </ImpressionWatcher>
          </React.Fragment>
        );
      })}

      {showViewAll && !altLook && viewAll}
    </Card>
  );
};

const appendUrlParams = (url: string, params: Record<string, string>) => {
  const urlObj = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    urlObj.searchParams.append(key, value);
  });
  return urlObj.toString();
};

export default RelatedWorksCard;
